import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Modal, Typography } from 'antd';
import { CloseOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import { ReadOnlyGuard } from '@spark';
import CustomCriteriaEditor from './CustomCriteriaEditor';
import { BUILTIN_JUDGE_CRITERIA } from './llmJudgeDefaults';
import BuiltinCriteriaViewer from './BuiltinCriteriaViewer';

const { Text } = Typography;

const normalizeKey = (s) => {
  if (!s) return '';
  return String(s)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const makeCustomIdFromName = (name) => `custom_${normalizeKey(name) || 'custom'}`;

const CriteriaRow = ({ item, onToggle, onOpen }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <Checkbox checked={!!item.enabled} onChange={(e) => onToggle(item.id, e.target.checked)} />
        <div>
          <Text style={{ fontSize: 14, fontWeight: 400 }}>{item.title}</Text>
          <div style={{ color: '#8c8c8c', fontSize: 14, fontWeight: 300 }}>
            {item.subtitle}
          </div>
        </div>
      </div>
      <Button type="text" icon={<RightOutlined />} onClick={() => onOpen(item)} />
    </div>
  );
};

const LLMJudgeConfigModal = ({ open, onClose, value, onChange }) => {
  const judgeModelDisplay = value?.judgeModel || 'Auto-selected';

  const initial = useMemo(() => {
    const builtins = Array.isArray(value?.builtins) ? value.builtins : BUILTIN_JUDGE_CRITERIA;
    const custom = Array.isArray(value?.custom) ? value.custom : [];
    const enabledMap = (value?.enabledById && typeof value.enabledById === 'object') ? value.enabledById : {};
    const normalizedBuiltins = builtins.map(b => ({ ...b, enabled: enabledMap[b.id] ?? b.enabled }));
    const normalizedCustom = custom.map(c => ({ ...c, enabled: enabledMap[c.id] ?? c.enabled ?? true }));
    return { builtins: normalizedBuiltins, custom: normalizedCustom };
  }, [value]);

  const [screen, setScreen] = useState('list'); // list | custom | builtin
  const [draft, setDraft] = useState(initial);
  const [editingCustom, setEditingCustom] = useState(null);
  const [viewingBuiltin, setViewingBuiltin] = useState(null);

  // Reset draft when opened
  useEffect(() => {
    if (!open) return;
    setDraft(initial);
    setScreen('list');
    setEditingCustom(null);
    setViewingBuiltin(null);
  }, [open, initial]);

  const items = [...draft.builtins, ...draft.custom];

  const toggle = (id, enabled) => {
    setDraft(prev => ({
      ...prev,
      builtins: prev.builtins.map(i => i.id === id ? { ...i, enabled } : i),
      custom: prev.custom.map(i => i.id === id ? { ...i, enabled } : i),
    }));
  };

  const openEditor = (item) => {
    if (item?.type === 'builtin') {
      setViewingBuiltin(item);
      setScreen('builtin');
      return;
    }

    if (item?.type === 'custom') {
      setEditingCustom(item);
    } else {
      setEditingCustom({
        id: 'custom_new',
        type: 'custom',
        title: 'Custom',
        subtitle: 'Write your own prompt - own evaluation criteria',
      });
    }
    setScreen('custom');
  };

  // Build the persistence payload from a given draft and trigger onChange.
  // Used both by the outer Ok and by inner editors that should save immediately.
  const persist = (nextDraft) => {
    const enabledById = [...nextDraft.builtins, ...nextDraft.custom].reduce((acc, i) => {
      acc[i.id] = !!i.enabled;
      return acc;
    }, {});
    onChange?.({
      custom: nextDraft.custom,
      enabledById,
    });
  };

  const saveAndClose = () => {
    persist(draft);
    onClose?.();
  };

  return (
    <Modal
      open={open}
      footer={null}
      width={520}
      onCancel={onClose}
      closeIcon={false}
      styles={{ body: {} }}
      zIndex={3000}
    >
      {screen === 'builtin' ? (
        <BuiltinCriteriaViewer
          title={viewingBuiltin?.title || ''}
          promptTemplate={viewingBuiltin?.promptTemplate}
          onBack={() => setScreen('list')}
          onCancel={onClose}
          onOk={() => {
            setScreen('list');
          }}
        />
      ) : screen === 'custom' ? (
        <CustomCriteriaEditor
          initialValues={{
            name: editingCustom?.title && editingCustom.title !== 'Custom' ? editingCustom.title : '',
            promptTemplate: editingCustom?.promptTemplate,
          }}
          onBack={() => setScreen('list')}
          onCancel={onClose}
          onOk={(values) => {
            const isEditingExisting = draft.custom.some(c => c.id === editingCustom?.id);
            const newCustom = {
              id: isEditingExisting ? editingCustom.id : makeCustomIdFromName(values.name),
              type: 'custom',
              title: values.name || 'Custom',
              subtitle: 'Write your own prompt - own evaluation criteria',
              promptTemplate: values.promptTemplate,
              enabled: true,
            };

            setDraft(prev => ({
              ...prev,
              custom: isEditingExisting
                ? prev.custom.map(c => (c.id === editingCustom.id ? { ...c, ...newCustom } : c))
                : [...prev.custom, newCustom],
            }));
            setScreen('list');
          }}
        />
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontWeight: 500, fontSize: 16 }}>LLM as a Judge Configuration</Text>
            <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
          </div>

          <div style={{ marginTop: 20 }}>
            <Text style={{ fontSize: 14, fontWeight: 400 }}>Judge Model (auto-selected)</Text>
            <div style={{
              marginTop: 8,
              padding: '5px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              background: '#fafafa',
              color: '#595959',
              fontSize: 14,
            }}>
              {judgeModelDisplay}
            </div>
            <div style={{ color: '#8c8c8c', fontSize: 14, fontWeight: 300, marginTop: 8 }}>
              The judge model is automatically selected based on your model provider
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <Text style={{ fontSize: 14, fontWeight: 400 }}>Evaluation Criteria</Text>
          </div>

          <div style={{ marginTop: 10 }}>
            {items.map(item => (
              <CriteriaRow
                key={item.id}
                item={item}
                onToggle={toggle}
                onOpen={openEditor}
              />
            ))}

            <Button
              type="link"
              icon={<PlusOutlined />}
              style={{ paddingInline: 0, marginTop: 4 }}
              onClick={() => openEditor({ type: 'custom' })}
            >
              Add custom criteria
            </Button>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 22 }}>
            <Button onClick={onClose}>Cancel</Button>
            <ReadOnlyGuard>
              <Button type="primary" onClick={saveAndClose}>Ok</Button>
            </ReadOnlyGuard>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default LLMJudgeConfigModal;

